import { Dayjs } from '@ui';
import { SMS, WHATSAPP } from '@utils/constants';

export interface IObjectKeys {
  [key: string]: any;
}

export type FileMap = Map<string, File>;

export type TermInsuranceProduct =
  | 'I_PROTECT_SMART'
  | 'I_PROTECT_SMART_PLUS'
  | 'SMART_SECURE_PLUS'
  | 'CLICK_2_PROTECT_LIFE'
  | 'CLICK_2_PROTECT_SUPER'
  | 'CLICK_2_PROTECT_SUPREME'
  | 'CLICK_2_PROTECT_SUPREME_PLUS'
  | 'SMART_TOTAL_ELITE'
  | 'E_TOUCH'
  | 'SMART_TERM_PLAN_PLUS';

export interface CoverSuggestionRequest {
  dob: string;
  gender: string | undefined;
  diabetic: boolean | undefined;
  annInc: number;
  mExpenses: number;
  tobacco: boolean;
  require_only?: string[];
}

export interface PremiumEstimateRequest {
  dob: string;
  gender: string | undefined;
  tobacco: boolean;
  cover: number;
  tillAge: number;
  diabetic: boolean;
  pincode: string;
  require_only?: string[];
  paymentFreq?: string;
}

export interface PremiumDistribution {
  policyTerm?: number;
  basePlan: {
    base: number;
    tax: number;
  };
  adhb: {
    base: number;
    tax: number;
  };
  acb: {
    base: number;
    tax: number;
  };
  ci: {
    base: number;
    tax: number;
  };
  ib?: {
    base: number;
    tax: number;
  };
  wop?: {
    base: number;
    tax: number;
  };
  total: {
    base: number;
    tax: number;
  };
  tillAge?: number;
  discount_included?: boolean;
  freq?: string;
}

export interface Premium extends PremiumDistribution {
  frequency: string;
  paymentType: string;
  tobacco: boolean;
  paymentTerm: number;
  policyTerm: number;
}

export interface PayOptionRequest {
  dob: string;
  gender: string | undefined;
  tobacco: boolean | undefined;
  cover: number; // min is 50L. No limit on max. needs to be an integer
  tillAge: number; // can be anything till 85 and after that only 99. Min. is currentAge+5
  paymentFreq: string; //default is monthly
  adhb?: number; // max is cover. min is 0. default is 0
  acb?: number; // max is cover. min is 0. default is 0
  ci?: number; // max is cover. min is 0. default is 0
  annInc: number;
  mExpenses: number;
  product: TermInsuranceProduct;
  diabetic: boolean;
}

export interface CIVariant {
  variant?: string;
}

export interface MaxCiDetails extends CIVariant {
  tpd?: boolean;
}

export interface TermProductFinalizeRequest {
  user: {
    phone_num: string;
    otp: string;
    full_name: string;
  };
  product_details: {
    product: TermInsuranceProduct;
    dob: string;
    gender: string;
    tobacco: boolean;
    diabetic: boolean;
    cover: number;
    tillAge: number;
    adhb: number;
    acb: number;
    ci: number;
    ci_details?: MaxCiDetails;
    ib: number;
    wop: boolean;
    paymentType: string;
    paymentFreq: string;
    annInc: number;
    pincode?: string;
  };
}

export interface TermKycInfoParams {
  product: TermInsuranceProduct;
  pan: string;
  dob: string;
  confirm?: boolean;
}

export type Relation = 'Self' | 'Spouse' | 'Father' | 'Mother' | 'Kids';

export interface HealthMemberCombo {
  name: string;
  description: string;
  relations: Relation[];
}

export interface KidsCountObject {
  sons: number;
  daughters: number;
}

export interface LoginFormFields {
  mobile: string;
  country: Country;
}

export interface RegFormFields {
  fullName: string;
  mobile: string;
  country: Country;
}

export type OtpMedium = typeof SMS | typeof WHATSAPP;

export interface RegFormUser {
  // Login Form does not include the fullName
  fullName?: string;
  mobile: string;
  country: Country;
  otp: string;
}

export type RegUser = Omit<RegFormUser, 'country'> & {
  country_code: string;
};

export interface RegUserMigration extends RegUser {
  email: string;
}

export interface ProviderFeature {
  title: string;
  desc: string;
  icon: string;
}

export interface RelatedMember {
  relation: string;
  age?: number;
}

export interface HealthCoverSuggestionRequest {
  members: RelatedMember[];
  pincode: string;
  ped: string;
}

export interface HealthPremiumEstimateRequest extends HealthCoverSuggestionRequest {
  sum_insured?: number;
  plan_type?: string;
}

export interface UserHealthPlan {
  sum_insured: number;
  deductible?: number;
  members: RelatedMember[];
  product?: string;
  tenure?: number;
  emi_opted?: boolean;
  emi_frequency?: string;
  sgb?: boolean;
  hcb?: boolean;
  cs?: boolean;
  rped?: boolean;
  rped_tenure?: number;
  urc?: boolean;
  ncb?: boolean;
  mty?: boolean;
  planType?: string;
  clms?: boolean;
  ic?: boolean;
  opd_care?: boolean;
  hmc?: boolean;
  hcb_cash?: number;
  // Annual Health Checkup
  ahc?: boolean;
}

export interface HealthProductFinalizeRequest {
  user: {
    phone_num: string;
    otp: string;
    full_name: string;
  };
  product: string;
  product_details: {
    sum_insured: number;
    members: RelatedMember[];
    pincode: string;
    tenure: number;
    emi_opted?: boolean;
    emi_frequency?: string;
    sgb: boolean;
    hcb: boolean;
    cs: boolean;
    rped: boolean;
    rped_tenure?: number;
    urc: boolean;
    ncb: boolean;
    deductible?: number;
    planType?: string;
    hcb_cash?: number;
  };
}

export interface HealthAddOn {
  name: string;
  cost: number;
  added: boolean | undefined;
}

export interface HealthPremiumRequest {
  sum_insured: number;
  pincode: string;
  members: RelatedMember[];
  product: string;
  ped: string;
  deductible?: number;
  emi_opted?: boolean;
  plan_type?: string;
  dependentAddons?: {
    ncb?: boolean;
    ic?: boolean;
    rped_tenure?: any;
    hcb_cash?: number;
  };
  porting?: boolean;
}

export type ParamTypes = {
  policyID: string;
};

export interface CustomPremium {
  total: number;
  tenure: number;
  quotePincode?: number;
  emi_frequency?: string;
  // Whenever pincode changes - insurer may not allow OPD rider at the new pincode
  // hence we need to track the updated status OPD rider whenever premium changed due to pincode
  opdRider?: undefined | boolean;
  newPremium?: any;
  addOnsImpacted?: Record<string, string>[];
  newPincode?: string;
  invalidApplication?: boolean;
}

export interface SelectOptions {
  label: string;
  value: string | number | boolean;
}

export interface ButtonSwitcherOptions {
  label: string;
  value: string | number | boolean;
  labelRight?: string;
}

export interface TermProductFeature {
  icon: string;
  title: string;
  desc: string;
}
export interface TermProductCardFeature {
  icon: string;
  text: string;
}

export interface TermProductMainBenefits {
  value: string;
  title: string;
  desc: string;
  icon: string;
  disclaimer?: string;
}

export interface HealthProductFeature {
  icon: string;
  desc: string;
}

export interface HealthProductMainFeature {
  icon: string;
  title: string;
  desc: string;
  info?: string;
}

export interface HealthProductCardFeature {
  icon: string;
  text: string;
  isUsp?: boolean;
  isDefect?: boolean;
}

export interface HealthProductWaitingPeriod {
  title: string;
  desc: string;
  icon: string;
  list?: string[];
}

export interface HealthProductMissingFeature {
  text: string;
  icon: string;
}

export interface DependentAddons {
  sgb?: boolean;
  hcb?: boolean;
  cs?: boolean;
  rped?: boolean;
  rped_tenure?: number;
  urc?: boolean;
  ncb?: boolean;
  mty?: boolean;
  clms?: boolean;
  ic?: boolean;
  ahc?: boolean;
  hmc?: boolean;
  hcb_cash?: number;
}

export type DependentAddon = keyof DependentAddons;

export interface FastProductDetails {
  productName: string;
  productSlug: string;
}

// Product Details stored in the Backend
export interface ProductDetails {
  sum_insured: number;
  tenure: number;
  cs: boolean;
  hmc: boolean;
  gender: string;
  salaried: boolean;
  ped: string;
  members: Member[];
  pincode: string;
  diabetic: boolean;
  opd_care: boolean;
  is_nri: boolean;
  gst_waiver: boolean;
  nri_country: string;
  ci_details?: CIVariant;
  porting?: boolean;
  plan_type?: HEALTH_PRODUCT_PLAN_TYPES;
}

export interface Member {
  age: number;
  relation: Relation;
  member_id: string;
}

export interface Answerable {
  answer: boolean;
}

export interface OnsetAnswer extends Answerable {
  onset?: any;
}

export interface DescriptiveOnsetAnswer extends OnsetAnswer {
  description?: null | string;
}

export interface AppRoute {
  path: string;
  component: React.ExoticComponent;
}

export enum QuoteType {
  Normal = 'normal',
  Fast = 'fast'
}
export interface HealthVerifyPincodeResponse {
  data: HealthPincodeVerificationData;
  success: boolean;
}

export enum HealthPincodeZones {
  zone1 = 'zone1',
  zone2 = 'zone2',
  delhi_ncr = 'delhi_ncr',
  hyd_sec = 'hyd_sec'
}

export interface HealthPincodeVerificationData {
  data: string;
  zone: HealthPincodeZones[];
}

// Document upload Status
export type DocStatus = 'added' | 'uploading' | 'uploaded' | 'failed';

export type CountryCallingCode = {
  name: string;
  calling_code: string;
  code: string;
};

export type Country = CountryCallingCode;

// Health product plan types
export enum HEALTH_PRODUCT_PLAN_TYPES {
  BRONZE_PLUS = 'Bronze +',
  PLATINUM_PLUS = 'Platinum +',
  TITANIUM_PLUS = 'Titanium +',
  NXT = 'Nxt',
  MAX = 'Max',
  PLUS = 'Plus',
  PREFERRED = 'Preferred',
  BLACK = 'Black'
}

export enum HEALTH_INSURANCE_PROVIDERS_SLUGS {
  aditya_birla = 'aditya-birla',
  niva_bupa = 'niva-bupa',
  hdfc_ergo = 'hdfc-ergo',
  care = 'care'
}

// Enums for term insuracne provider names (identifiers used in req/res payloads)
export enum TERM_INSURANCE_PROVIDERS {
  ICICIPRU = 'ICICIPRU',
  MAXLIFE = 'MAXLIFE',
  HDFCLIFE = 'HDFCLIFE',
  BALIC = 'BALIC'
}

// Term insurance provider slugs used in urls
export enum TERM_INSURANCE_PROVIDERS_SLUGS {
  max_life = 'max-life',
  hdfc_life = 'hdfc-life',
  icicipru = 'icicipru',
  balic = 'bajaj-life'
}

// Max life insurance product slugs
export enum MAX_INSURANCE_PRODUCTS {
  smart_secure_plus = 'smart-secure-plus',
  smart_total_elite = 'smart-total-elite',
  smart_term_plan_plus = 'smart_term_plan_plus'
}

// HDFC life insurance product slugs
export enum HDFCLIFE_INSURANCE_PRODUCTS {
  click_2_protect_life = 'click-2-protect-life',
  click_2_protect_super = 'click-2-protect-super',
  click_2_protect_supreme = 'click-2-protect-supreme',
  click_2_protect_supreme_plus = 'click-2-protect-supreme-plus'
}

// ICICI prudential insurance product slugs
export enum ICICIPRU_INSURANCE_PRODUCTS {
  i_protect_smart = 'i-protect-smart',
  i_protect_smart_plus = 'i-protect-smart-plus'
}

// BALIC insurance product slugs
export enum BALIC_INSURANCE_PRODUCTS {
  e_touch = 'e-touch'
}

// Type safe insurance provider slug to product slug mapping
type _TermInsuranceProducts = {
  [TERM_INSURANCE_PROVIDERS_SLUGS.max_life]: MAX_INSURANCE_PRODUCTS;
  [TERM_INSURANCE_PROVIDERS_SLUGS.hdfc_life]: HDFCLIFE_INSURANCE_PRODUCTS;
  [TERM_INSURANCE_PROVIDERS_SLUGS.icicipru]: ICICIPRU_INSURANCE_PRODUCTS;
  [TERM_INSURANCE_PROVIDERS_SLUGS.balic]: BALIC_INSURANCE_PRODUCTS;
};

export type TermInsuranceProviderConfigs<Config> = {
  [P in TERM_INSURANCE_PROVIDERS_SLUGS]: Record<_TermInsuranceProducts[P], Config>;
};

export enum ADITYA_BIRLA_INSURANCE_PRODUCTS_SLUGS {
  activ_one = 'activ-one',
  activ_fit = 'activ-fit',
  activ_one_vytl = 'activ-one-vytl'
}

export enum NIVABUPA_INSURANCE_PRODUCTS_SLUGS {
  reassure = 'reassure',
  reassure_3 = 'reassure3',
  companion = 'companion',
  recharge = 'recharge'
}

export enum HDFC_ERGO_INSURANCE_PRODUCTS_SLUGS {
  optima_secure = 'optima-secure',
  optima_secure_plus = 'optima-secure-plus',
  optima_super_secure = 'optima-super-secure',
  optima_restore = 'optima-restore',
  energy_silver = 'energy-silver',
  medisure_topup = 'medisure-topup'
}

export enum CARE_INSURANCE_PRODUCTS_SLUGS {
  supreme = 'supreme',
  ultimate = 'ultimate',
  freedom = 'freedom',
  advantage = 'advantage',
  youth_plus = 'youth-plus',
  complete_plus = 'complete-plus',
  heart = 'heart'
}

type _HealthInsuranceProducts = {
  [HEALTH_INSURANCE_PROVIDERS_SLUGS.aditya_birla]: ADITYA_BIRLA_INSURANCE_PRODUCTS_SLUGS;
  [HEALTH_INSURANCE_PROVIDERS_SLUGS.niva_bupa]: NIVABUPA_INSURANCE_PRODUCTS_SLUGS;
  [HEALTH_INSURANCE_PROVIDERS_SLUGS.hdfc_ergo]: HDFC_ERGO_INSURANCE_PRODUCTS_SLUGS;
  [HEALTH_INSURANCE_PROVIDERS_SLUGS.care]: CARE_INSURANCE_PRODUCTS_SLUGS;
};

export type HealthInsuranceProviderConfigs<Config> = {
  [P in HEALTH_INSURANCE_PROVIDERS_SLUGS]: Record<
    _HealthInsuranceProducts[P],
    Config
  >;
};

export enum ProposalType {
  term = 'term',
  health = 'health'
}

export const ResponseStatusCodes = {
  BAD_REQUEST: 400,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  SERVER_ERROR: 500
} as const;

export const OS = {
  WINDOWS: 'Windows',
  MAC: 'Mac',
  LINUX: 'Linux',
  IOS: 'iOS',
  ANDROID: 'Android'
} as const;

// Types and Enums for Security and Login
export enum LoginType {
  HEALTH = 'health',
  TERM = 'term',
  DITTO_ACCOUNT = 'dittoaccount',
  GENERAL = 'general'
}

export interface LoginTypeInfo {
  type: LoginType;
  internal_id: string | null;
}

export interface LoginSecurity extends LoginTypeInfo {
  turnstile_token: string;
}

export type DateField = Dayjs | null;

/**
 * @description
 * transforms the type of keys with DateField check  type to string.
 *
 * @example
```
type FooBar = {
            foo: string;
            bar: DateField;
          }

type FooBar = DateFieldsToString<FooBar>

typeof FooBarWithStringDates = {
            foo: string;
            bar: string;
          }
```
 */
export type DateFieldsToString<T> = {
  [Property in keyof T]: T[Property] extends DateField ? string : T[Property];
};
